import { useState } from 'react'
import { useParams, useLoaderData } from 'react-router-dom'
import { usePreview, useBrowserPermissions } from '@/hooks'
import { useParticipantStatus } from '@/hooks/useParticipantStatus'
import { useVideoState } from '@/hooks/useVideoState'
import { useAppointmentPageRefresh } from '@/hooks/useAppointmentPageRefresh'
import type { PreJoinLoaderData } from '@/app/loaders/prejoinLoader'
import { MeetingHeader } from '@/components/molecules/Header/MeetingHeader'
import { WaitingRoomCard } from '@/components/organisms/WaitingRoomCard'
import { ConsentCard } from '@/components/molecules/ConsentCard'
import { PermissionWarningCard } from '@/components/molecules/PermissionWarningCard/PermissionWarningCard'
import { PermissionWarningModal } from '@/components/molecules/PermissionWarningModal/PermissionWarningModal'
import { AppTemplate } from '@/components/templates/AppTemplate/AppTemplate'
import { BrowserVideoPreview } from '@/components/atoms/BrowserVideoPreview/BrowserVideoPreview'

export const VideoWaitingRoom: React.FC = () => {
  const { id: appointmentId } = useParams<{ id: string }>()
  const normalizedAppointmentId = appointmentId?.trim() ?? ''

  // Load appointment data via loader
  const { appointment: appointmentData } = useLoaderData() as PreJoinLoaderData

  // Preview and device management (browser-native, no Chime session needed)
  const { preview, join, isJoining, videoStream, isVideoLoading, isMuted, toggleMute } = usePreview({
    appointmentId: normalizedAppointmentId
  })

  // Determine video state for visual indicator
  const videoState = useVideoState({
    isVideoEnabled: preview.isVideoPreviewActive,
    videoError: preview.videoError,
    selectedVideoInput: preview.selectedVideoInput
  })

  // Check if other participant is already in meeting
  const { isParticipantPresent } = useParticipantStatus()

  // Program automatic page refresh at appointment start + 10s
  useAppointmentPageRefresh({
    appointmentFrom: appointmentData.from,
    currentTimeStatus: appointmentData.timeStatus
  })

  // Check browser permissions for camera and microphone
  const { hasPermissions, camera, microphone } = useBrowserPermissions()
  const isCameraPermissionDenied = camera !== 'granted'
  const isMicrophonePermissionDenied = microphone !== 'granted'

  // Modal state
  const [showPermissionModal, setShowPermissionModal] = useState(false)

  // Use backend status directly (no more local override)
  const timeStatus = appointmentData.timeStatus

  const handleJoinMeeting = async () => {
    if (!normalizedAppointmentId) return

    // Check if permissions are not granted (denied or prompt)
    // Show modal if user tries to join without permissions
    if (isCameraPermissionDenied || isMicrophonePermissionDenied) {
      setShowPermissionModal(true)
      return
    }

    // Proceed with joining if permissions are granted
    await proceedWithJoin()
  }

  const proceedWithJoin = async () => {
    if (!normalizedAppointmentId) return

    try {
      await join({
        options: {
          audioInputId: preview.selectedAudioInput,
          videoInputId: preview.selectedVideoInput,
          audioOutputId: preview.selectedAudioOutput,
          startVideo: preview.isVideoPreviewActive,
          startMuted: isMuted,
          mode: 'video'
        }
      })
    } catch (joinError) {
      console.error('[WaitingRoom] Failed to join meeting:', joinError)
    }
  }

  const handleModalConfirm = () => {
    setShowPermissionModal(false)
    void proceedWithJoin()
  }

  const handleModalClose = () => {
    setShowPermissionModal(false)
  }

  const noCameraAvailable = preview.videoInputs.length === 0
  const noMicrophoneAvailable = preview.audioInputs.length === 0

  const participant = appointmentData.interlocutor

  if (!participant) {
    return (
      <AppTemplate header={<MeetingHeader showVideocallTag />}>
        <div className="flex items-center justify-center p-8">
          <p className="text-secondary">Aucun participant trouvé pour ce rendez-vous.</p>
        </div>
      </AppTemplate>
    )
  }

  return (
    <AppTemplate header={<MeetingHeader showVideocallTag />}>
      <div className="flex w-full max-w-7xl flex-col md:gap-6 h-[calc(100dvh-56px)] lg:h-auto">
        {/* Permission Warning Card - shown when permissions are not granted */}
        {!hasPermissions && <PermissionWarningCard />}

        {/* Main Waiting Room Card */}
        <WaitingRoomCard
          // Video Setup Props
          isVideoActive={preview.isVideoPreviewActive}
          isMicrophoneActive={!isMuted}
          noCameraAvailable={noCameraAvailable}
          noMicrophoneAvailable={noMicrophoneAvailable}
          onToggleVideo={() => void preview.toggleVideoPreview()}
          onToggleMicrophone={toggleMute}
          videoInputs={preview.videoInputs.map((device) => ({
            id: device.id,
            label: device.label
          }))}
          audioInputs={preview.audioInputs.map((device) => ({
            id: device.id,
            label: device.label
          }))}
          audioOutputs={preview.audioOutputs.map((device) => ({
            id: device.id,
            label: device.label
          }))}
          selectedVideoInput={preview.selectedVideoInput}
          selectedAudioInput={preview.selectedAudioInput}
          selectedAudioOutput={preview.selectedAudioOutput}
          onVideoInputChange={(id: string) => void preview.selectVideoInput({ id })}
          onAudioInputChange={(id: string) => void preview.selectAudioInput({ id })}
          onAudioOutputChange={(id: string) => void preview.selectAudioOutput({ id })}
          audioLevel={preview.audioLevel}
          videoState={videoState}
          previewComponent={<BrowserVideoPreview stream={videoStream} isLoading={isVideoLoading} />}
          isCameraPermissionDenied={isCameraPermissionDenied}
          isMicrophonePermissionDenied={isMicrophonePermissionDenied}
          // Appointment Info Props
          participantName={participant.firstName}
          participantAvatar={participant.avatar ?? undefined}
          scheduledDate={appointmentData.from}
          isParticipantPresent={appointmentData.hasInterlocutorJoined || isParticipantPresent}
          conversationId={appointmentData.conversationId}
          timeStatus={timeStatus}
          onJoinMeeting={handleJoinMeeting}
          isJoining={isJoining}
        />

        {/* Consent Card */}
        <ConsentCard className="hidden lg:flex" phoneNumber={participant.phone} />
      </div>

      {/* Permission Warning Modal */}
      <PermissionWarningModal
        isOpen={showPermissionModal}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        cameraPermissionDenied={isCameraPermissionDenied}
        microphonePermissionDenied={isMicrophonePermissionDenied}
      />
    </AppTemplate>
  )
}
